import { merge } from 'lodash';
import { cookies, localStorage } from 'app/lib/storage';
import Tenancy from 'app/lib/tenancy';
import { CookieUniqueId } from 'app/config/constants';
import * as Sentry from '@sentry/browser';
import { addBreadcrumb } from 'app/lib/sentry';
import { getQueryParams } from 'app/lib/utils';

const isHeyDoctor = Tenancy.isHeyDoctor();
const isGoodRx = Tenancy.isGoodRx();
const partner = cookies.get()?.queryParams?.partner || Tenancy.getTenant();
const grxUniqueId = cookies.get()[CookieUniqueId.GRX];
const SEGMENT_OVERRIDES = {
  context: { ip: '0.0.0.0', Optimizely: { userId: grxUniqueId } },
};

export const trackEvent = (event: any, eventData: any = undefined, options: { bypassSegment?: boolean } = {}) => {
  if (!options.bypassSegment && checkWindowAnalytics('track')) {
    try {
      const eventProps = {
        ...eventData,
        partner,
      };

      if (process.env.BRANCH) {
        eventProps.branch = process.env.BRANCH;
      }

      window.analytics.track(event, eventProps, merge({}, options, SEGMENT_OVERRIDES));
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
    }
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, eventData });
  }
};

export const trackScreen = (page?: { path?: string; url?: string }) => {
  if (checkWindowAnalytics('page')) {
    window.analytics.page(
      null,
      null,
      { partner, ...page },
      {
        ...SEGMENT_OVERRIDES,
        context: {
          campaign: getCampaignInfo(),
          ...SEGMENT_OVERRIDES.context,
        },
      }
    );
  }

  if (isGoodRx) {
    trackEvent('contentView', {
      pagePathWithQuery: window.location,
    });
  }

  addBreadcrumb(window.location);
};

export const identifyUser = (id = null) => {
  if (checkWindowAnalytics('identify')) {
    const userId = id || cookies.get()['ajs_user_id'];
    const anonymousId = cookies.get()['ajs_anonymous_id']?.replace(/%22/g, '');
    //if we have passed id from server, and this is first initiation
    if (anonymousId && !localStorage?.get('ajs_anonymous_id')) {
      window.analytics.setAnonymousId(anonymousId); //@see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#override-the-default-anonymous-id-with-a-call
    }

    try {
      window.analytics.identify(
        userId,
        {
          client: Tenancy.getTenant(),
          environment: process.env.STAGE,
          anonymousId,
        },
        SEGMENT_OVERRIDES
      );
    } catch (error) {
      Sentry.captureException(error);
    }
  }
};

export const checkWindowAnalytics = (method = 'track') => {
  if (!process.env.SEGMENT_WRITE_KEY) {
    return false;
  }

  if (window.analytics && typeof window.analytics[method] === 'function') {
    return true;
  }

  Sentry.captureMessage(`Trying to track event when window.analytics.${method} is not ready`);

  return false;
};

export const getCampaignInfo = (): { medium?: string; source?: string } => {
  const campaignMedium = getMedium();
  const campaignSource = getSource();

  return {
    ...(campaignMedium && { medium: campaignMedium }),
    ...(campaignSource && { source: campaignSource }),
    ...utm(),
  };
};
/**
 * Get the type of referrals (e.g. email, cpc, etc.)
 * @see https://firstdigital.co.nz/blog/google-analytics/retrieve-traffic-sources-data-without-google-analytics-cookies/
 */
const getMedium = (): string => {
  const queryParams = {
    ...cookies.get().queryParams,
    ...getQueryParams(),
  };

  const searchEngines = ['bing', 'google', 'yahoo', 'baidu', 'yandex', 'ask'];

  const ref = document.referrer ? new URL(document.referrer) : undefined;

  let medium;
  if (queryParams.utm_medium) {
    medium = queryParams.utm_medium;
  } else if (queryParams.gclid) {
    medium = 'cpc';
  } else if (!isRefSameDomain(ref)) {
    medium = 'referral';

    if (searchEngines.find((s) => ref?.hostname.includes(s))) {
      medium = 'organic';
    }
  } else {
    medium = cookies.get().utmMedium;
  }

  // save utmMedium in cookies, see https://app.asana.com/0/0/1199078472778749/f
  if (medium) {
    cookies.set('utmMedium', medium);
  }

  return medium;
};

const getSource = (): string => {
  const queryParams = {
    ...cookies.get().queryParams,
    ...getQueryParams(),
  };

  const ref = document.referrer ? new URL(document.referrer) : undefined;

  let source;
  if (queryParams.utm_source) {
    source = queryParams.utm_source;
  } else if (!isRefSameDomain(ref)) {
    source = ref?.hostname;
  } else {
    source = cookies.get().utmSource;
  }

  // save utmSource in cookies, see https://app.asana.com/0/0/1199078472778749/f
  if (source) {
    cookies.set('utmSource', source);
  }

  return source;
};

/**
 * Get all utm params
 * from https://github.com/segmentio/utm-params/blob/master/lib/index.js
 */
export const utm = () => {
  const params = getQueryParams();
  const results = {};
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      if (key.substr(0, 4) === 'utm_') {
        let param = key.substr(4);
        if (param === 'campaign') param = 'name';
        results[param] = params[key];
      }
    }
  }

  return results;
};

const isRefSameDomain = (ref?: URL): boolean =>
  ref ? ref.hostname.replace('www.', '') === window.location.hostname.replace('www.', '') : true;

const trackQuora = (event: any) => {
  if (window.qp && typeof window.qp === 'function') {
    window.qp('track', event);
  }
};

export const onCouponViewed = (coupon: any, shortCode: string) => {
  const eventName = isHeyDoctor ? 'Coupon Viewed' : 'couponViewed';
  const eventData = {
    coupon: {
      memberId: coupon.memberId,
      rxGroup: coupon.groupNumber,
      rxBin: coupon.rxbin,
      rxPcn: coupon.rxpcn,
    },
    visit: { shortCode },
  };

  trackEvent(eventName, eventData, { bypassSegment: true });
};

export const onUserSignIn = ({ id, method = 'email' }: any) => {
  identifyUser(id);
  trackEvent('User Login', { id, method });
};

export const onUserSignUp = ({ id, method = 'email' }: any) => {
  identifyUser(id);
  trackQuora('CompleteRegistration');
  trackEvent('User Registered', { id, method });
  if (isGoodRx) {
    trackEvent('userAction', {
      registrationCompleted: 'true',
    });
  }
};

export const onUserUpdated = () => {
  trackEvent('User Updated');
};

export const onUserForgotPassword = () => {
  trackEvent('Password Forgotten');
};

export const onUserResetPassword = () => {
  trackEvent('Password Reset');
};

export const onMessageSent = () => {
  trackEvent('Message Sent');
};

export const onVisitStarted = () => {
  trackEvent('Visit Started');
  trackQuora('GenerateLead');
};

/* eslint-disable camelcase */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const onTaskCompleted = ({ task_code }: any) => {
  trackEvent('Task Completed', { task_code });

  // track separate event for each task in optimizely
  if (window.optimizelyClientInstance && typeof window.optimizelyClientInstance.track === 'function') {
    window.optimizelyClientInstance.track(`${task_code} Task Completed`);
  }
};
/* eslint-enable camelcase */

export const onVisitCompleted = () => trackEvent('Visit Completed');

export const onPaymentReceived = ({ amount, ...rest }: any) => {
  trackQuora('Purchase');
  trackEvent('Visit Payment', {
    ...rest,
    price: amount,
    revenue: amount,
    currency: 'USD',
  });
  if (isGoodRx) {
    trackEvent('userAction', {
      checkoutCompleted: 'true',
    });
  }
};

export const onNotificationUpdated = (data: any) => trackEvent('Notification Updated', data);

export const onDeliveryAvailable = () => trackEvent('Delivery Available');
export const onDeliverySelected = () => trackEvent('Delivery Selected');
export const onDeliveryPayment = () => trackEvent('Delivery Payment');
export const onDeliveryUpdated = () => trackEvent('Delivery Updated');
export const onDeliveryCancelled = () => trackEvent('Delivery Cancelled');
export const onTermsAgreement = () => trackEvent('Terms Agreement');
export const onHmaAgreement = () => trackEvent('Hma Agreement');
export const onDeliveryDrawerClosed = () => trackEvent('Delivery Window Dismissed');
export const onEditHomeAddress = () => trackEvent('Edit Home Address');
export const onNpiLearnMore = () => trackEvent('Provider NPI Learn More');
export const onDeliveryLearnMore = () => trackEvent('Mail Delivery Learn More');

export const onServiceClick = (serviceName: string, location = 'all') =>
  trackEvent('Service Clicked', { name: serviceName, location });

export type ConsentSource =
  | 'Sign Up'
  | 'Edit Profile'
  | 'Consent Modal - Get Coupons'
  | 'Consent Modal - Pharmacy Selection';

export const onHmaConsent = ({ source }: { source: ConsentSource }) => trackEvent('Consented to HMA', { source });

export const onEducationMessageViewed = (data: { id: number; title: string }) =>
  trackEvent('Education Message Viewed', data);

export const onEducationMessageClicked = (data: { id: number; title: string }) =>
  trackEvent('Education Message Clicked', data);

export const onCreditCardFieldFocused = () => trackEvent('Credit Card Field Focused');
export const onPromoCodeApplied = () => trackEvent('Promo Code Applied');
export const onPromoCodeFailed = () => trackEvent('Promo Code Failed');
export const onPromoCodeFieldFocused = () => trackEvent('Promo Code Field Focused');
export const onPromoCodeLinkClicked = () => trackEvent('Promo Code Link Clicked', { buttonName: 'Promo Code Link' });
export const onPaymentAgreementLinkClicked = () => trackEvent('Payment Agreement Link Clicked');
export const onPaymentRequestButtonDisplayed = () => trackEvent('Payment Request Button Displayed');

export const onPaymentRequestSubmitted = (data: { methodName?: string }) =>
  trackEvent('Payment Request Submitted', data);

export const onPaymentRequestFailed = (data: { methodName?: string; message?: string }) =>
  trackEvent('Payment Request Failed', data);

export const onStripeError = (data: { message?: string; code?: string }) =>
  trackEvent('Stripe Error', {
    ...(data.message && { errorMessage: data.message }),
    ...(data.code && { errorCode: data.code }),
  });

export const onTriageCtaInteraction = (data: {
  type: 'exit-chat' | 'view-goodrx-marketplace' | 'start-visit';
  sessionId: string;
}) => trackEvent('Triage Cta Interaction', data);

export const onTriageEmptyResults = (data: { sessionId: string; searchTerm: string }) =>
  trackEvent('Triage No Symptom Search Results', data);

export const onTriageBotLoaded = (data: { sessionId: string }) => trackEvent('Triage Bot Loaded', data);

export const onVideoQueueEnter = () => trackEvent('Start Video Visit Clicked');
export const onVideoHelpCenterClick = () => trackEvent('Video Help Center Link Clicked');
export const onVideoHangupClick = () => trackEvent('Video Hangup Button Clicked');
export const onVideoSessionEndedModalDisplayed = () => trackEvent('Session Ended Modal Displayed');
export const onVideoConfirmSessionEndedClicked = () => trackEvent('Confirm Session Ended Clicked');
export const onVideoChatCompleteModalDisplayed = () => trackEvent('Video Chat Complete Modal Displayed');
export const onVideoChatCompleteNextClicked = () => trackEvent('Video Chat Complete Next Clicked');
export const onVideoNotifyMeClicked = () => trackEvent('Video Notify Me Clicked');
export const onVideoGotItClick = () => trackEvent('Video Got It Button Clicked');
export const onVideoPatientAndDoctorConnected = () => trackEvent('Patient and Doctor Connected');
export const onVideoConfirmNotificationModalDisplayed = () => trackEvent('Confirm Notification Modal Displayed');
export const onVideoConfirmNotificationModalConfirmClicked = () =>
  trackEvent('Confirm Notification Modal Confirm Clicked');
export const onVideoErrorModalShow = (data: { message: string }) => trackEvent('Video Error Modal Show', data);
export const onCameraPermissionsAllowed = () => trackEvent('Camera Permissions Allowed');
export const onCameraPermissionsError = (data: { message: string }) => trackEvent('Camera Permissions Error', data);
export const onMicPermissionsAllowed = () => trackEvent('Mic Permissions Allowed');
export const onMicPermissionsError = (data: { message: string }) => trackEvent('Mic Permissions Error', data);

export const onPhoneTaskCompleted = () => trackEvent('Phone Task Completed');
